const createCirclePath = (offsetX: number, offsetY: number, radius: number) => {
    // prettier-ignore
    return `M ${offsetX} ${offsetY} m -${radius}, 0 a ${radius},${radius} 0 1,0 ${radius * 2},0 a ${radius},${radius} 0 1,0 -${radius * 2},0 Z`;
};

const createRectanglePath = (offsetX: number, offsetY: number, width: number, height: number) => {
    return `M ${offsetX} ${offsetY} H ${offsetX + width} V ${offsetY + height} H ${offsetX} Z`;
};

const createRoundedRectanglePath = (
    offsetX: number,
    offsetY: number,
    width: number,
    height: number,
    borderRadius: number,
) => {
    const w1 = Math.max(width - 2 * borderRadius, 0);
    const h1 = Math.max(height - 2 * borderRadius, 0);
    const r = Math.max(Math.min((width - w1) / 2, (height - h1) / 2), 0);
    const w = width - 2 * r;
    const h = height - 2 * r;
    const x = offsetX;
    const y = offsetY;

    if (r === 0) {
        return createRectanglePath(x, y, w, h);
    }

    // https://stackoverflow.com/questions/10177985/svg-rounded-corner
    // prettier-ignore
    return `m ${x + r} ${y} h ${w} a ${r} ${r} 0 0 1 ${r} ${r} v ${h} a ${r} ${r} 0 0 1 -${r} ${r} h -${w} a ${r} ${r} 0 0 1 -${r} -${r} v -${h} a ${r} ${r} 0 0 1 ${r} -${r} z`;
};

const createEllipsePath = (offsetX: number, offsetY: number, cx: number, cy: number, rx: number, ry: number) => {
    rx = Math.max(rx, 0);
    ry = Math.max(ry, 0);

    if (!rx && !ry) {
        return '';
    }

    return `m ${offsetX + (cx - rx)} ${offsetY + cy} a ${rx} ${ry} 0 1 0 ${rx * 2} 0 a ${rx} ${ry} 0 1 0 -${
        rx * 2
    } 0 z`;
};

const createTrianglePath = (offsetX: number, offsetY: number, p1X: number, p1Y: number, p2X: number, p2Y: number) => {
    // draw triangle between offset, p1 and p2
    return `M ${offsetX} ${offsetY} L ${p1X} ${p1Y} L ${p2X} ${p2Y} Z`;
};

const generateCurvedPath = (points, precision = 2) => {
    if (!points || points.length < 2) {
        return '';
    }

    const path = [];
    const round = (num) => Number(num.toFixed(precision));

    for (let i = 0; i < points.length; i++) {
        const prev = points[(i - 1 + points.length) % points.length];
        const curr = points[i];
        const next = points[(i + 1) % points.length];

        const r = Math.min(curr.r || 0, distance(curr, prev) / 2, distance(curr, next) / 2);

        if (r === 0) {
            if (i === 0) {
                path.push(`M ${round(curr.x)} ${round(curr.y)}`);
            } else {
                path.push(`L ${round(curr.x)} ${round(curr.y)}`);
            }

            continue;
        }

        const start = getPointOnLine(curr, prev, r);
        const end = getPointOnLine(curr, next, r);

        if (i === 0) {
            path.push(`M ${round(start.x)} ${round(start.y)}`);
        } else {
            path.push(`L ${round(start.x)} ${round(start.y)}`);
        }

        const sweep = isClockwise(prev, curr, next) ? 0 : 1;
        path.push(`A ${round(r)} ${round(r)} 0 0 ${sweep} ${round(end.x)} ${round(end.y)}`);
    }

    path.push('Z');

    return path.join(' ');
};

function distance(p1, p2) {
    return Math.hypot(p2.x - p1.x, p2.y - p1.y);
}

function getPointOnLine(p1, p2, dist) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const len = Math.hypot(dx, dy);

    return {
        x: p1.x + (dx / len) * dist,
        y: p1.y + (dy / len) * dist,
    };
}

function isClockwise(p1, p2, p3) {
    const cross = (p2.x - p1.x) * (p3.y - p2.y) - (p2.y - p1.y) * (p3.x - p2.x);

    return cross < 0;
}

export {
    createCirclePath,
    createRectanglePath,
    createTrianglePath,
    createRoundedRectanglePath,
    generateCurvedPath,
    createEllipsePath,
};
